import { io, Socket } from 'socket.io-client';

/**
 * Singleton socket.io client. Connects to the same origin as the
 * REST API and uses the session cookie for auth on the handshake.
 *
 * Pattern:
 *   - Lazy: nothing connects until `getSocket()` is first called.
 *   - Cookie auth: the gateway extracts `knowlez_session` from
 *     `Cookie:` on the handshake. With `withCredentials: true` the
 *     browser ships the cookie automatically because CORS is
 *     configured to allow it.
 *   - Single instance: re-calling `getSocket()` returns the same
 *     instance and re-connects only if it has been disconnected.
 *   - Server pushes: every event is broadcast as a `CustomEvent` on
 *     `window` with the same name. Components listen via
 *     `window.addEventListener('notification:new', e => ...)` so
 *     React Query can invalidate without owning a socket reference.
 *
 *   import { ensureSocket } from '@/lib/socket';
 *   useEffect(() => { ensureSocket(); }, []);
 */

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '';

let socket: Socket | null = null;
let bound = false;

export function ensureSocket(): Socket {
  if (socket && socket.connected) return socket;
  if (!socket) {
    socket = io(API_BASE || window.location.origin, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      // We let the browser ship the session cookie on the handshake;
      // no separate auth token is required.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 30_000,
    });
    if (!bound) bindRebroadcast(socket);
    bound = true;
  } else if (!socket.connected) {
    socket.connect();
  }
  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
    bound = false;
  }
}

export function getSocket(): Socket | null {
  return socket;
}

/**
 * Re-emit every meaningful server event as a window `CustomEvent`.
 * This keeps the socket layer independent of React Query — components
 * subscribe via `window.addEventListener('notification:new', …)` and
 * call their own `queryClient.invalidateQueries(...)`.
 */
function bindRebroadcast(s: Socket): void {
  const events = ['notification:new', 'notification:read', 'workspace:updated'];
  for (const ev of events) {
    s.on(ev, (payload: unknown) => {
      window.dispatchEvent(new CustomEvent(ev, { detail: payload }));
    });
  }
  s.on('auth_error', (payload: unknown) => {
    window.dispatchEvent(new CustomEvent('auth_error', { detail: payload }));
  });
}
